/* eslint-disable @typescript-eslint/explicit-function-return-type */
import axios from 'ggApp/utils/requests';
import stepIdentifierMapper, {
    mapBISteps,
} from 'ggApp/modules/newCheckout/utils/stepIdentifierMapper';
import orderUpdateMutation from 'ggApp/shared/context/Order/services/cartServiceAPI/order/orderUpdate.graphql';
import { CheckoutMachine } from 'ggApp/shared/context/Order/services/integration';
import { OrderModes } from 'ggApp/shared/context/Order/types';
import { CHECKOUT_STEP_NAMES } from 'ggApp/modules/newCheckout/constants';
import { sendToGtm } from 'ggApp/modules/tracking/actions';
import { ANALYTIC_EVENTS } from 'ggApp/modules/gaAnalytics/constants';
import CookiesManager from 'ggApp/utils/CookiesManager';
import { IP_ADDRESS_COOKIE_NAME } from 'ggApp/utils/ipAddressCookie/constants';
import { sendCheckoutStep } from 'snowplow';
import { getCheckoutFlow, getStepFromState, getDataFromContext, getOrderData } from './utils';

interface PayloadModel {
    newCheckoutFlag: { variantKey: string };
}

// export for testing
export enum hookTypes {
    enter = 'enter',
    close = 'exit.modal_close',
    leave = 'exit.confirm',
    stop = 'stopCheckout', // from machine state
}

const getHook = (hookType: hookTypes, payload: PayloadModel) => (
    context: CheckoutMachine,
    event: any,
    machineState: any,
): void => {
    // if you know, why hook is fired for these events, fix it
    if (['REMOVE_LINE_ITEM', 'done.invoke.removeLineItem'].includes(event?.type || '')) {
        return;
    }
    const { userId, orderId } = getDataFromContext(context);
    const cookiesManager = new CookiesManager();
    const ipAddress = cookiesManager.get(IP_ADDRESS_COOKIE_NAME);
    const { newCheckoutFlag } = payload || {};
    const checkoutSteps = [...context.steps];
    const step =
        hookType === hookTypes.enter ? getStepFromState(machineState.state) : checkoutSteps.pop();
    const previousStep = checkoutSteps.pop();
    const flowName = getCheckoutFlow(newCheckoutFlag);

    const orderData =
        step === CHECKOUT_STEP_NAMES.ORDER_CONFIRMATION
            ? getOrderData({
                  price: context.cart && (context.cart.totalPrice as number),
                  items: (context.cart && context.cart.items) || [],
                  isFlex: context.orderMode === OrderModes.Flex,
                  storeId: context.activeStore.store_id,
              })
            : {};

    const eventPayload = {
        eventAction: stepIdentifierMapper(step),
        eventLabel: hookType,
        eventCategory: 'checkout',
        eventProperty: {
            current_flow: flowName,
            orderID: orderId as string,
            userID: userId as number,
            client_ip_address: ipAddress === 'unknown' ? null : ipAddress,
            ...(previousStep && {
                previous_step: stepIdentifierMapper(previousStep),
            }),
            ...orderData,
        },
    };
    sendCheckoutStep(eventPayload);
    sendToGtm({ event: ANALYTIC_EVENTS.gtmEvent, ...eventPayload });
};

export const getTrackingHooks = (payload: PayloadModel) => {
    return {
        onEnter: getHook(hookTypes.enter, payload),
        onExit: getHook(hookTypes.leave, payload),
        onClose: getHook(hookTypes.close, payload),
    };
};

export const updateFlexOrderStepState = async ({ orderNumber, currentStep }) => {
    const trackingStep = mapBISteps(currentStep);
    return axios.patch(`orders/${orderNumber}`, { step: trackingStep }).then((res) => res.data);
};

export const updateMixOrderStepState = async ({ orderNumber, currentStep }) => {
    const trackingStep = mapBISteps(currentStep);
    try {
        const { CartServiceClient } = await import(
            'ggApp/shared/context/Order/services/cartServiceAPI/client'
        );
        const { data } = await CartServiceClient.mutation({
            mutation: orderUpdateMutation,
            variables: { orderNumber, step: trackingStep },
        });

        return data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const updateOrderStepState = ({ orderMode, orderNumber, currentStep }) => {
    if (orderMode === OrderModes.Flex) {
        updateFlexOrderStepState({ orderNumber, currentStep });
    }
    if (orderMode === OrderModes.Mix) {
        updateMixOrderStepState({ orderNumber, currentStep });
    }
};
